import { useCallback } from "react";
import dayjs from "dayjs";
import { useSettings } from "../useSetting";
import { ISetting } from "@/domain/setting.interface";
import { displayDateOfBirth } from "../../utils/temporal.utils";

type DateFormat = "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD";

/**
 * Hook for date formatting based on the countryFormat account setting.
 * Uses Temporal.PlainDate under the hood to avoid timezone offset issues.
 */
export const useDateFormat = () => {
	const { data: settings } = useSettings();
	const { countryFormat } = settings || ({} as ISetting);
	const dateFormat =
		((countryFormat as { dateFormat?: string })?.dateFormat as DateFormat) ||
		"MM/DD/YYYY";

	/**
	 * Format a date of birth for display.
	 * Handles both legacy ISO dates ("1993-07-02T06:00:00.000Z")
	 * and already-formatted dates ("07/02/1993").
	 */
	const formatDob = useCallback(
		(dateString: string | null | undefined): string =>
			displayDateOfBirth(dateString, dateFormat) || "-",
		[dateFormat],
	);

	const formatDate = useCallback(
		(value: string | Date | null | undefined): string =>
			value ? dayjs(value).format(dateFormat) : "-",
		[dateFormat],
	);

	const formatDateTime = useCallback(
		(value: string | Date | null | undefined): string =>
			value ? dayjs(value).format(`${dateFormat} hh:mm A`) : "—",
		[dateFormat],
	);

	return { dateFormat, formatDob, formatDate, formatDateTime };
};
